import React, { useMemo, useState } from 'react';
import {
  ArrowsClockwise,
  MagnifyingGlass,
  Minus,
  Plus,
  Seal,
  X,
  CheckCircle,
} from '@phosphor-icons/react';
import { Tooltip } from 'react-tooltip';
import colors from '@Common/assets/colors';
import { format, parseISO } from 'date-fns';
import { useAnalyzerReportsContext } from '@Context/AnalyzerReportsContext';
import {
  getCurrentWeekRange,
  getPreviousWeekRange,
  groupTicketsByConfidence,
} from '@Utils/analyzerReportsUtils';
import { TicketCard } from './TicketCard';
import { MoonLoader } from 'react-spinners';
import { JiraPodReport } from '@Models/types';
import { ProjectDropdown } from './ProjectDropdown';
import { PromptDropdown } from './PromptDropdown';
import { computeTicketCost } from '@Utils/tokenCostUtils';
import { useAuth } from '@Context/CognitoAuthProvider';

const formatDuration = (duration: string): string => {
  try {
    const parts = duration.split(' to ');
    if (parts.length === 2) {
      const startDate = format(parseISO(parts[0].trim()), 'MM-dd-yyyy');
      const endDate = format(parseISO(parts[1].trim()), 'MM-dd-yyyy');
      return `${startDate} to ${endDate}`;
    }
    return duration;
  } catch (error) {
    return duration;
  }
};

export const ReportList: React.FC<{
  expandedWeek: string[];
  setExpandedWeek: React.Dispatch<React.SetStateAction<string[]>>;
}> = ({ expandedWeek, setExpandedWeek }) => {
  const {
    reports,
    loading,
    searchReportDate,
    selectedTickets,
    setSelectedTickets,
    setSearchReportDate,
    handleManualRefetch,
    selectedTicket,
    setSelectedTicket,
    availableProjects,
    selectedProject,
    setSelectedProject,
    markReportEvaluated,
    evaluationLoading,
  } = useAnalyzerReportsContext();

  const [expandedSectionsMap, setExpandedSectionsMap] = useState<
    Record<string, Record<string, boolean>>
  >({});

  const toggleSection = (week: string, level: string) => {
    setExpandedSectionsMap((prev) => ({
      ...prev,
      [week]: {
        ...prev[week],
        [level]: !prev[week]?.[level],
      },
    }));
  };

  const projectLatestReportEvaluated: Record<string, boolean> = useMemo(() => {
    const { week: previousWeek } = getPreviousWeekRange();
    const result: Record<string, boolean> = {};

    reports.forEach((report) => {
      const projectId = report.project.key;

      if (report.week === previousWeek) {
        result[projectId] = report.evaluated;
      }
    });

    return result;
  }, [reports]);

  const filteredReports = reports.filter(
    (report) =>
      report.project.key === selectedProject &&
      (report.week.toLowerCase().includes(searchReportDate.toLowerCase()) ||
        report.duration.toLowerCase().includes(searchReportDate.toLowerCase()))
  );

  const toggleWeek = (week: string) => {
    setExpandedWeek((prev) =>
      prev.includes(week) ? prev.filter((w) => w !== week) : [...prev, week]
    );
  };

  const isTicketSelected = (ticket: JiraPodReport['tickets'][0]) => {
    return !!selectedTickets[ticket.ticket.key];
  };

  const handleCheckboxChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    t: JiraPodReport['tickets'][0],
    week: string
  ) => {
    setSelectedTickets((prev) => {
      const copy = { ...prev };
      if (e.target.checked) {
        copy[t.ticket.key] = { ticket: t, week };
      } else {
        delete copy[t.ticket.key];
      }
      return copy;
    });
  };

  const [ticketSearch, setTicketSearch] = useState<Record<string, string>>({});

  const handleTicketSearchChange = (week: string, value: string) => {
    setTicketSearch((prev) => ({ ...prev, [week]: value }));
  };

  const { user } = useAuth();
  const isAdmin = user?.admin;

  return (
    <div className="custom-scroll flex h-full w-4/5 flex-col gap-3 overflow-auto px-2">
      <div className="flex items-center justify-between">
        <div className="flex flex-row items-center gap-x-2">
          <div className="flex w-full flex-row items-center gap-x-2 rounded-md border px-4 py-2">
            <MagnifyingGlass size={20} weight="bold" />
            <input
              type="text"
              className="w-full border-gray-300 font-inter text-xs focus:outline-none sm:text-sm"
              placeholder={`Search for records`}
              value={searchReportDate}
              onChange={(e) => setSearchReportDate(e.target.value)}
            />
            <span
              className="cursor-pointer hover:text-brandRed-500"
              onClick={() => setSearchReportDate('')}
            >
              <X weight="bold" />
            </span>
          </div>
          <ProjectDropdown
            availableProjects={availableProjects}
            selectedProject={selectedProject}
            setSelectedProject={setSelectedProject}
            projectLatestReportEvaluated={projectLatestReportEvaluated}
            isAdmin={isAdmin}
          />

          {isAdmin && <PromptDropdown isAdmin={isAdmin} />}
        </div>
        <button
          onClick={handleManualRefetch}
          disabled={loading}
          className="rounded-full p-2 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-50"
          title="Refresh reports"
        >
          <ArrowsClockwise
            size={24}
            weight="fill"
            className={loading ? 'animate-spin text-gray-600' : 'text-gray-600'}
          />
        </button>
      </div>

      {isAdmin && filteredReports.length !== 0 && (
        <div className="flex flex-row items-center gap-2 whitespace-nowrap px-5 text-xs">
          <p>Assigned to:</p>
          <p className="italic">
            {filteredReports[0].evaluator}@trajectorservices.com
          </p>
        </div>
      )}
      {loading && (
        <span className="flex h-full w-full flex-col items-center justify-center sm:gap-y-6 sm:p-5">
          <MoonLoader color="#071D49" size={100} />
          <span className="animate-pulse text-xs font-semibold tracking-wide text-gray-600 sm:text-sm">
            Fetching Reports
          </span>
        </span>
      )}
      {!loading && filteredReports.length === 0 && (
        <p className="p-4 italic">No Reports Found</p>
      )}
      {!loading &&
        filteredReports.length !== 0 &&
        filteredReports.map((report) => {
          const isOpen = expandedWeek.includes(report.week);
          const grouped = groupTicketsByConfidence(report.tickets);
          const evaluated = report.evaluated;
          const searchValue = ticketSearch[report.week] || '';
          const { week: previousWeek } = getPreviousWeekRange();
          const { week: currentWeek } = getCurrentWeekRange();
          const expandedSections = expandedSectionsMap[report.week] ?? {
            high: true,
            mid: false,
            low: false,
            unclassified: false,
          };

          const filterTickets = (tickets: JiraPodReport['tickets']) =>
            tickets.filter((t) =>
              t.ticket.key.toLowerCase().includes(searchValue.toLowerCase())
            );

          const totalBlendedCost = report.tickets.reduce(
            (sum, t) => sum + computeTicketCost(t).blendedCost,
            0
          );
          const totalInputCost = report.tickets.reduce(
            (sum, t) => sum + computeTicketCost(t).inputCost,
            0
          );
          const totalOutputCost = report.tickets.reduce(
            (sum, t) => sum + computeTicketCost(t).outputCost,
            0
          );
          const evalKey = `${report.week}_${report.project.key}`;
          const isEvalLoading = !!evaluationLoading[evalKey];
          return (
            <div
              key={report.week}
              className={`rounded-2xl border p-5 shadow-md hover:bg-gray-100 ${
                evaluated
                  ? 'border-green-500 bg-green-50'
                  : 'border-gray-200 bg-gray-50'
              }`}
            >
              <button
                className="flex w-full items-center justify-between p-4 text-left"
                onClick={() => toggleWeek(report.week)}
              >
                <div className="flex flex-col items-start gap-2">
                  {!projectLatestReportEvaluated[report.project.key] &&
                    previousWeek === report.week && (
                      <div className="font-bold italic text-brandError-500">
                        For evaluation
                      </div>
                    )}

                  {report.week === currentWeek && (
                    <div className="font-bold italic text-brandWarning-400">
                      In Progress
                    </div>
                  )}

                  <div className="flex w-full flex-row items-center gap-2">
                    <h2 className="text-lg font-bold">
                      Report for {report.week}
                    </h2>
                    <span className="text-sm font-normal text-gray-500">
                      • {formatDuration(report.duration)}
                    </span>
                  </div>
                  <span className="flex items-center gap-2">
                    {evaluated && (
                      <span className="flex items-center gap-1 font-semibold text-green-600">
                        <CheckCircle size={20} weight="fill" />
                        Evaluated
                        {report.evaluatedAt && (
                          <span className="ml-2 text-xs text-gray-400">
                            ({new Date(report.evaluatedAt).toLocaleString()})
                          </span>
                        )}
                      </span>
                    )}
                  </span>
                </div>
                <span className="text-sm text-primary-500">
                  {isOpen ? <Minus size={32} /> : <Plus size={32} />}
                </span>
              </button>

              {isOpen && (
                <div className="space-y-6">
                  <div className="mb-2 flex flex-row items-center gap-2">
                    <button
                      className={`whitespace-nowrap rounded-md px-4 py-2 text-xs font-semibold transition-colors ${
                        evaluated
                          ? 'bg-green-500 text-white hover:bg-green-600'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      } ${isEvalLoading ? 'cursor-not-allowed opacity-60' : ''}`}
                      onClick={() =>
                        markReportEvaluated(report.week, report.project.key)
                      }
                      disabled={isEvalLoading}
                    >
                      {isEvalLoading ? (
                        <span className="flex items-center gap-2">
                          <span className="inline-block h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                          Processing...
                        </span>
                      ) : evaluated ? (
                        'Mark as Not Evaluated'
                      ) : (
                        'Mark as Evaluated'
                      )}
                    </button>

                    <input
                      type="text"
                      className="w-full rounded-md border border-gray-300 px-4 py-2 font-inter text-xs focus:outline-none sm:text-sm"
                      placeholder="Search ticket ref#"
                      value={searchValue}
                      onChange={(e) =>
                        handleTicketSearchChange(report.week, e.target.value)
                      }
                    />
                  </div>

                  <div>
                    <div className="flex flex-row items-center gap-4 text-xs">
                      <span>Input: ${totalInputCost.toFixed(4)}</span>
                      <span>Output: ${totalOutputCost.toFixed(4)}</span>
                    </div>
                    <div className="flex flex-row items-center gap-4">
                      <div className="text-xs text-gray-500">
                        <span className="font-medium">Total Cost:</span>{' '}
                        <span>${totalBlendedCost.toFixed(4)}</span>
                        <span className="ml-2 text-gray-400">(blended)</span>
                      </div>
                    </div>
                  </div>

                  {(['high', 'mid', 'low', 'unclassified'] as const).map(
                    (level) => {
                      const label =
                        level === 'high'
                          ? 'High Confidence (70-100%)'
                          : level === 'mid'
                            ? 'Mid Confidence (30-69%)'
                            : level === 'low'
                              ? 'Low Confidence (<30%)'
                              : 'Unclassified';
                      const color =
                        level === 'high'
                          ? 'border-green-500'
                          : level === 'mid'
                            ? 'border-yellow-400'
                            : level === 'low'
                              ? 'border-red-400'
                              : 'border-gray-400';
                      const confidenceTickets = filterTickets(grouped[level]);
                      if (confidenceTickets.length === 0) return null;
                      return (
                        <div key={level}>
                          <button
                            onClick={() => toggleSection(report.week, level)}
                            className={`border-l-4 pl-3 text-left text-lg font-semibold ${color} mb-2 w-full`}
                          >
                            {label} ({confidenceTickets.length}){' '}
                            <span className="ml-2 text-sm text-gray-500">
                              {expandedSections[level] ? '▲ Hide' : '▼ Show'}
                            </span>
                          </button>

                          {expandedSections[level] && (
                            <div className="grid gap-4 md:grid-cols-3">
                              {confidenceTickets.map((t) => (
                                <button
                                  key={t.ticket.key}
                                  onClick={() => setSelectedTicket(t)}
                                  className={`rounded-2xl border bg-white p-4 shadow transition hover:shadow-lg ${
                                    t.ticket.tagged
                                      ? 'opacity-50'
                                      : 'border-gray-200'
                                  }`}
                                >
                                  <div className="mb-1 flex items-center justify-between gap-2">
                                    <div className="flex min-w-0 flex-1 items-center gap-2">
                                      <span className="flex-shrink-0">
                                        <input
                                          type="checkbox"
                                          checked={isTicketSelected(t)}
                                          disabled={t.ticket.tagged}
                                          onClick={(e) => e.stopPropagation()}
                                          onChange={(e) =>
                                            handleCheckboxChange(
                                              e,
                                              t,
                                              report.week
                                            )
                                          }
                                        />
                                      </span>
                                      <div className="flex min-w-0 flex-1 flex-col items-start text-left">
                                        <h3 className="w-full truncate text-sm font-semibold">
                                          {t.ticket.key}
                                        </h3>
                                        <p
                                          className="line-clamp-2 w-full text-xs font-normal text-gray-500"
                                          data-tooltip-id={`ticket-name-${t.ticket.key}`}
                                          data-tooltip-content={
                                            t.ticket.summary
                                          }
                                          data-tooltip-place="top"
                                        >
                                          {t.ticket.summary}
                                        </p>
                                        <Tooltip
                                          id={`ticket-name-${t.ticket.key}`}
                                          style={{
                                            color: 'white',
                                            background: colors.primary[700],
                                            borderRadius: '0.5rem',
                                            maxWidth: '250px',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                          }}
                                          className="font-light"
                                        />
                                      </div>
                                    </div>
                                    <span className="flex-shrink-0 text-sm text-gray-500">
                                      {t.ticket.tagged ? (
                                        <span className="flex flex-row items-center gap-x-1 text-xs italic">
                                          <Seal
                                            size={20}
                                            weight="fill"
                                            className="text-blue-500"
                                          />
                                          <label className="text-blue-400">
                                            Tagged
                                          </label>
                                        </span>
                                      ) : t.ticket.new_ticket ? (
                                        <span className="flex flex-row items-center gap-x-1 text-xs italic">
                                          <Seal
                                            size={20}
                                            weight="fill"
                                            className="text-green-500"
                                          />
                                          <label className="text-green-400">
                                            New
                                          </label>
                                        </span>
                                      ) : null}
                                    </span>
                                  </div>
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      );
                    }
                  )}
                </div>
              )}
              {selectedTicket && (
                <div
                  onClick={() => setSelectedTicket(null)}
                  className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40"
                >
                  <div
                    onClick={(e) => e.stopPropagation()}
                    className="relative h-3/4 w-3/4 rounded-2xl border border-gray-200 bg-white p-4 shadow-xl"
                  >
                    <TicketCard ticket={selectedTicket} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
    </div>
  );
};
